/**
 * Class for managing the retailers map functionality
 */
class RetailersMap extends HTMLElement {
  // CSS selectors
  static selectors = {
    mapContainer: '#retailersMap',
  };

  constructor() {
    super();
    this.mapContainer = this.querySelector(RetailersMap.selectors.mapContainer);
    this.mapboxToken =
      this.dataset.mapboxToken ||
      'pk.eyJ1IjoiZW1tYW51ZWxvbGVhc3NlbmNlIiwiYSI6ImNsMWt4NTllZzA1NHIzZG82amJjaGZzcGcifQ.zQPdY1tZvFjMDd0VKtLS1A';
    this.mapStyle = this.dataset.mapStyle || 'mapbox://styles/emmanueloleassence/cl1kx7rho000g14nw2x39x6da';
    this.centerCoordinates = this.dataset.centerCoordinates ? JSON.parse(this.dataset.centerCoordinates) : [2, 46.5];
    this.zoomLevel = parseFloat(this.dataset.zoomLevel || 4.5);
    this.markers = [];
  }

  /**
   * Callback when the element is connected to the DOM
   */
  connectedCallback() {
    if (!this.mapContainer) {
      console.error('Map container not found');
      return;
    }

    // Initialize the map when the element is connected to the DOM
    this.initMap();
  }

  /**
   * Initialize the Mapbox map
   */
  initMap() {
    if (!window.mapboxgl) {
      console.error('Mapbox GL JS is not loaded');
      return;
    }

    // Set the Mapbox access token
    mapboxgl.accessToken = this.mapboxToken;

    // Create the map instance
    this.map = new mapboxgl.Map({
      container: this.mapContainer.id,
      style: this.mapStyle,
      center: this.centerCoordinates,
      zoom: this.zoomLevel,
    });

    // Disable scroll zoom for better UX
    this.map.scrollZoom.disable();

    // Add navigation controls
    this.map.addControl(new mapboxgl.NavigationControl());

    // Add markers when the map is loaded
    this.map.on('load', () => {
      this.addMarkers();
    });
  }

  /**
   * Get the GeoJSON data from the script tag
   */
  getGeoJsonData() {
    // Get the ID of the current element
    const elementId = this.id;

    // Find the script tag containing the GeoJSON data
    const scriptElement = document.getElementById(`${elementId}-geojson`);

    if (!scriptElement) {
      console.error(`GeoJSON script element with ID "${elementId}-geojson" not found`);
      return null;
    }

    try {
      // Parse the JSON content of the script tag
      return JSON.parse(scriptElement.textContent);
    } catch (error) {
      console.error('Error parsing GeoJSON data from script tag:', error);
      return null;
    }
  }

  /**
   * Add markers to the map based on the GeoJSON data
   */
  addMarkers() {
    // Get the GeoJSON data from the script tag
    const geojson = this.getGeoJsonData();

    if (!geojson) {
      console.error('No valid GeoJSON data found');
      return;
    }

    try {
      // Add markers for each feature in the GeoJSON
      geojson.features.forEach((marker) => {
        // Create a HTML element for each marker
        const el = document.createElement('div');
        el.className = 'marker';

        // Create a popup with the marker information
        const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
          <div class="marker_label">
            <h5>${marker.properties.title}</h5>
            <p>${marker.properties.description}</p>
            <a href="${marker.properties.link}" class="button" target="_blank">${this.dataset.goThereText || 'Go there'}</a>
          </div>
        `);

        // Create and add the marker to the map
        const mapMarker = new mapboxgl.Marker(el)
          .setLngLat(marker.geometry.coordinates)
          .setPopup(popup)
          .addTo(this.map);

        // Store the marker reference
        this.markers.push(mapMarker);
      });
    } catch (error) {
      console.error('Error adding markers:', error);
    }
  }

  /**
   * Resize the map when the window size changes
   */
  resizeMap() {
    if (this.map) {
      this.map.resize();
    }
  }

  /**
   * Callback when the element is disconnected from the DOM
   */
  disconnectedCallback() {
    // Clean up resources when the element is removed
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
    this.markers = [];
  }
}

// Register the custom element
customElements.define('retailers-map', RetailersMap);
